import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEntityFlows } from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/**
 * R4 — the flows linked to one entity, rendered inside that entity's own scene.
 *
 * It earns its space or takes none: an empty list renders NOTHING, because a permanent empty
 * "Flows" block on every document in the estate is noise rather than information. An error is
 * silent for the same reason — a side-list that shouts about its own failure is worse than a
 * side-list that is simply absent.
 *
 * Every call rides the ApiClient adapter through `useEntityFlows`. A raw `fetch` here would
 * arrive anonymous under PKCE and 401 (the estate has paid for that lesson once already).
 */
export function EntityFlowsPanel({
  kind,
  entityId,
}: {
  kind: "collection" | "document";
  entityId: string;
}) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useEntityFlows(kind, entityId);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner label={t("Loading")} />
      </div>
    );
  }

  const flows = data?.flows ?? [];
  if (isError || flows.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-2 border-t border-divider pt-4">
      <Text as="h2" variant="small">
        {t("Linked flows")}
      </Text>
      <ul className="flex flex-col gap-2">
        {flows.map((flow) => (
          <li key={flow.id} className="flex min-w-0 items-baseline gap-2">
            {/* W3 — the row becomes a way in. A panel that names a flow and then makes you go
                find it in settings is a list, not a link. */}
            {flow.abilities[ABILITY.flowUpdate] ? (
              <Link
                to="/flows/$slug/edit"
                params={{ slug: flow.slug }}
                className="truncate text-accent text-body hover:underline"
              >
                {flow.name}
              </Link>
            ) : (
              <span className="truncate text-body text-text">{flow.name}</span>
            )}
            <span className="truncate font-mono text-small text-text-tertiary">{flow.slug}</span>
            {flow.categories.length > 0 ? (
              <span className="truncate text-small text-text-tertiary">
                {flow.categories.join(", ")}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
